import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { taxFilingsGateway } from '../index';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { sandboxTransmissionActionsEnabled } from '../../../shared/commercialActions';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import { useIdentity } from '../../identity/state/useIdentity';
import { TaxFormCode, taxFormCodeLabels, type AnnualFormResponse, type QuarterlyFormResponse, type TaxFilingResponse } from '../types';

const SUB_TABS = [
  { key: 'filings', label: 'Filings' },
  { key: 'forms', label: 'Quarterly & Annual Forms' },
] as const;
type SubTab = (typeof SUB_TABS)[number]['key'];

export function LegacyTaxFilingsPage() {
  const [subTab, setSubTab] = useState<SubTab>('filings');

  return (
    <section className="card card--wide">
      <div className="section-header">
        <h2>Tax Filings</h2>
        {sandboxTransmissionActionsEnabled && (
          <span className="badge badge--caution">Sandbox Simulation Only — No Agency Submission</span>
        )}
      </div>
      {sandboxTransmissionActionsEnabled && (
        <p className="muted">
          Filing submission is bookkeeping only (no dollar amounts, no agency contact) — see{' '}
          <code>docs/tax-filings.md</code>. A prepared filing must be approved or denied by a different user
          before it reaches the (Mock) provider; creating one is idempotent per company/form/year/quarter.
        </p>
      )}

      <div className="tabs">
        {SUB_TABS.map((t) => (
          <button key={t.key} type="button" className={subTab === t.key ? 'tab tab--active' : 'tab'} onClick={() => setSubTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'forms' ? (
        <FormsPanel />
      ) : sandboxTransmissionActionsEnabled ? (
        <FilingsPanel />
      ) : (
        // EPN-98: no live filing provider exists, so preparing/approving a filing would only ever produce a
        // simulated "Accepted". Nothing is fetched or shown here — see shared/commercialActions.ts.
        <FeatureUnavailableNotice feature="Electronic tax filing">
          EPN is not connected to a live tax-filing provider in this release, so filings cannot be prepared,
          approved or transmitted here. No filing has been submitted to any tax agency from EPN.
        </FeatureUnavailableNotice>
      )}
    </section>
  );
}

function FilingsPanel() {
  const { me } = useIdentity();
  const [filings, setFilings] = useState<TaxFilingResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [formCode, setFormCode] = useState<number>(TaxFormCode.Form941);
  const [taxYear, setTaxYear] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(1);

  const [denyingId, setDenyingId] = useState<string | null>(null);
  const [denialReason, setDenialReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await taxFilingsGateway.listFilings();
      setFilings(list.sort((a, b) => b.preparedAtUtc.localeCompare(a.preparedAtUtc)));
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load tax filings.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusyId('__new__');
    setError(null);
    try {
      await taxFilingsGateway.submitFiling({ formCode, taxYear, quarter: formCode === TaxFormCode.Form941 ? quarter : null });
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not prepare the filing — one for this company/form/period may already exist.'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleApprove(id: string) {
    setBusyId(id);
    setError(null);
    try {
      await taxFilingsGateway.approveFiling(id);
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not approve the filing.'));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDeny(e: FormEvent) {
    e.preventDefault();
    if (!denyingId) return;
    setBusyId(denyingId);
    setError(null);
    try {
      await taxFilingsGateway.denyFiling(denyingId, denialReason);
      setDenyingId(null);
      setDenialReason('');
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not deny the filing.'));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}

      <form className="inline-form" onSubmit={handleSubmit} style={{ margin: '12px 0' }}>
        <select value={formCode} onChange={(e) => setFormCode(Number(e.target.value))}>
          <option value={TaxFormCode.Form941}>{taxFormCodeLabels[TaxFormCode.Form941]}</option>
          <option value={TaxFormCode.Form940}>{taxFormCodeLabels[TaxFormCode.Form940]}</option>
        </select>
        <input type="number" min="2020" max="2100" value={taxYear} onChange={(e) => setTaxYear(Number(e.target.value))} required />
        {formCode === TaxFormCode.Form941 && (
          <select value={quarter} onChange={(e) => setQuarter(Number(e.target.value))}>
            <option value={1}>Q1</option><option value={2}>Q2</option><option value={3}>Q3</option><option value={4}>Q4</option>
          </select>
        )}
        <button type="submit" disabled={busyId === '__new__'}>{busyId === '__new__' ? 'Preparing…' : 'Prepare filing'}</button>
      </form>

      {denyingId && (
        <form className="inline-form" onSubmit={handleDeny} style={{ marginBottom: 12 }}>
          <strong>Denial reason</strong>
          <input value={denialReason} onChange={(e) => setDenialReason(e.target.value)} required />
          <button type="submit">Confirm deny</button>
          <button type="button" className="link" onClick={() => setDenyingId(null)}>Cancel</button>
        </form>
      )}

      {loading && <p className="muted">Loading…</p>}

      {!loading && (
        <table>
          <thead><tr><th>Form</th><th>Period</th><th>Status</th><th>Prepared by</th><th>Reviewed by</th><th></th></tr></thead>
          <tbody>
            {filings.map((f) => {
              const isOwnFiling = me?.email === f.preparedByEmailSnapshot;
              return (
                <tr key={f.id}>
                  <td>{taxFormCodeLabels[f.formCode]}</td>
                  <td>{f.taxYear}{f.quarter ? ` Q${f.quarter}` : ''}</td>
                  <td>
                    {f.status}
                    {f.status === 'Rejected' && f.failureReason && <div className="muted">Reason: {f.failureReason}</div>}
                    {f.status === 'Denied' && f.denialReason && <div className="muted">Reason: {f.denialReason}</div>}
                  </td>
                  <td className="muted">{f.preparedByEmailSnapshot}</td>
                  <td className="muted">{f.reviewedByEmailSnapshot ?? '—'}</td>
                  <td>
                    {f.status === 'PendingApproval' && (
                      isOwnFiling ? (
                        <span className="muted">Awaiting a different reviewer — you prepared this one</span>
                      ) : (
                        <>
                          <button type="button" className="link" disabled={busyId === f.id} onClick={() => void handleApprove(f.id)}>Approve</button>
                          <button type="button" className="link" disabled={busyId === f.id} onClick={() => setDenyingId(f.id)}>Deny</button>
                        </>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
            {filings.length === 0 && <tr><td colSpan={6} className="muted">No filings prepared yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FormsPanel() {
  const [liabilities, setLiabilities] = useState<{ id: string; formCode: number; taxYear: number; quarter: number | null }[]>([]);
  const [liabilityId, setLiabilityId] = useState('');
  const [quarterlyForms, setQuarterlyForms] = useState<QuarterlyFormResponse[]>([]);
  const [annualForms, setAnnualForms] = useState<AnnualFormResponse[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [liabs, qforms, aforms] = await Promise.all([
        taxFilingsGateway.listLiabilities(), taxFilingsGateway.listQuarterlyForms(), taxFilingsGateway.listAnnualForms(),
      ]);
      setLiabilities(liabs);
      setQuarterlyForms(qforms.sort((a, b) => b.generatedAtUtc.localeCompare(a.generatedAtUtc)));
      setAnnualForms(aforms.sort((a, b) => b.generatedAtUtc.localeCompare(a.generatedAtUtc)));
      const firstLiability = liabs[0];
      if (firstLiability) setLiabilityId((prev) => prev || firstLiability.id);
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not load forms.'));
    }
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load();
  }, [load]);

  const selectedLiability = liabilities.find((l) => l.id === liabilityId) ?? null;

  async function handleGenerate() {
    if (!selectedLiability) return;
    setBusy(true);
    setError(null);
    try {
      if (selectedLiability.formCode === TaxFormCode.Form941) {
        await taxFilingsGateway.generateQuarterlyForm(selectedLiability.id, null);
      } else {
        await taxFilingsGateway.generateAnnualForm(selectedLiability.id, null);
      }
      await load();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'Could not generate the form.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      {error && <p className="error">{error}</p>}

      {liabilities.length === 0 ? (
        <p className="muted">No calculated tax liabilities yet — calculate one on the Payroll Taxes screen first.</p>
      ) : (
        <div className="inline-form" style={{ margin: '12px 0' }}>
          <select value={liabilityId} onChange={(e) => setLiabilityId(e.target.value)}>
            {liabilities.map((l) => (
              <option key={l.id} value={l.id}>{taxFormCodeLabels[l.formCode]} — {l.taxYear}{l.quarter ? ` Q${l.quarter}` : ''}</option>
            ))}
          </select>
          <button type="button" disabled={busy} onClick={() => void handleGenerate()}>
            {busy ? 'Generating…' : `Generate ${selectedLiability?.formCode === TaxFormCode.Form941 ? 'Form 941' : 'Form 940'}`}
          </button>
        </div>
      )}

      <h3>Quarterly (Form 941)</h3>
      <table>
        <thead><tr><th>Period</th><th>Wages</th><th>Tax Withheld</th><th>Deposits</th><th>Balance Due</th></tr></thead>
        <tbody>
          {quarterlyForms.map((f) => (
            <tr key={f.id}>
              <td>{f.taxYear} Q{f.quarter}</td>
              <td>{f.totalWages.toFixed(2)}</td>
              <td>{f.totalTaxWithheld.toFixed(2)}</td>
              <td>{f.totalDeposits.toFixed(2)}</td>
              <td>{f.balanceDue.toFixed(2)}</td>
            </tr>
          ))}
          {quarterlyForms.length === 0 && <tr><td colSpan={5} className="muted">None generated yet.</td></tr>}
        </tbody>
      </table>

      <h3>Annual (Form 940)</h3>
      <table>
        <thead><tr><th>Year</th><th>Wages</th><th>Tax Withheld</th><th>Deposits</th><th>Balance Due</th></tr></thead>
        <tbody>
          {annualForms.map((f) => (
            <tr key={f.id}>
              <td>{f.taxYear}</td>
              <td>{f.totalWages.toFixed(2)}</td>
              <td>{f.totalTaxWithheld.toFixed(2)}</td>
              <td>{f.totalDeposits.toFixed(2)}</td>
              <td>{f.balanceDue.toFixed(2)}</td>
            </tr>
          ))}
          {annualForms.length === 0 && <tr><td colSpan={5} className="muted">None generated yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
